"use client";

import { useEffect, useRef } from "react";
import { useDragDropContext } from "./DragDropContext";

interface DragChipProps {
  value: string;
  label?: string;
  disabled?: boolean;
  className?: string;
}

export function DragChip({ value, label, disabled, className = "" }: DragChipProps) {
  const { dragValue, startDrag } = useDragDropContext();
  const isDragging = dragValue === value;

  return (
    <button
      type="button"
      disabled={disabled}
      onPointerDown={(e) => {
        if (disabled) return;
        e.preventDefault();
        startDrag(value, e.clientX, e.clientY);
      }}
      className={`touch-none select-none rounded-2xl bg-white font-extrabold text-slate-800 shadow-md ring-2 ring-slate-200 transition active:scale-95 disabled:opacity-40 ${className} ${
        isDragging ? "opacity-30" : ""
      }`}
    >
      {label ?? value}
    </button>
  );
}

interface DropSlotProps {
  id: string;
  label?: string;
  value?: string | null;
  /** Chip id to drag back out of this slot (when editable). */
  dragValue?: string | null;
  editable?: boolean;
  onPickFromSlot?: (slotId: string) => void;
  className?: string;
}

export function DropSlot({
  id,
  label,
  value,
  dragValue,
  editable = false,
  onPickFromSlot,
  className = "",
}: DropSlotProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { registerSlot, unregisterSlot, highlightSlot, startDrag } = useDragDropContext();
  const active = highlightSlot === id;
  const canPick = editable && value && dragValue;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    registerSlot(id, el);
    return () => unregisterSlot(id);
  }, [id, registerSlot, unregisterSlot]);

  return (
    <div
      ref={ref}
      onPointerDown={(e) => {
        if (!canPick) return;
        e.preventDefault();
        e.stopPropagation();
        onPickFromSlot?.(id);
        startDrag(dragValue!, e.clientX, e.clientY);
      }}
      className={`flex flex-col items-center justify-center rounded-2xl border-2 border-dashed transition ${className} ${
        canPick ? "cursor-grab touch-none select-none active:cursor-grabbing" : ""
      } ${
        active
          ? "border-indigo-500 bg-indigo-50 scale-105"
          : value
            ? "border-green-400 bg-green-50"
            : "border-slate-300 bg-white/80"
      }`}
    >
      {value ? (
        <span className="text-2xl font-extrabold text-slate-800">{value}</span>
      ) : (
        <span className="text-center text-xs font-semibold text-slate-400">
          {label ?? "Drop here"}
        </span>
      )}
    </div>
  );
}
